<template>
    <div class="edit-section" ref="section">
        <div class="section-header">
            <div class="header-icon" v-if="icon">
                <img :src="require(`@/${icon}`)" />
            </div>
            <div
                :style="{ color: $Utils.colors[accent] }"
                class="header-title"
                v-if="title"
            >
                {{ $t(title) }}
            </div>
            <div class="tools-row" v-if="actions">
                <div
                    :key="`important${index}`"
                    :style="{ color: $Utils.colors[action.color] }"
                    @click="emitAction(action.action)"
                    class="list-action"
                    v-for="(action, index) in actions"
                >
                    <div
                        :style="{ background: $Utils.colors[action.color] }"
                        class="list-action-icon"
                        v-if="action.materialIcon"
                    >
                        <i class="material-icons">{{ action.materialIcon }}</i>
                    </div>
                    {{ action.display }}
                </div>
            </div>
        </div>
        <div class="section-content">
            <div
                :class="`-size-${section.size}`"
                :key="index"
                class="section-row"
                v-for="(section, index) in filteredSections"
            >
                <div style="display: flex; align-items: center">
                    <div class="row-title">{{ $t(sectionTitle(section)) }}</div>
                    <span class="row-hint">
                        <i class="material-icons" v-if="section.hint">{{
                            'help'
                        }}</i>
                        <div v-if="section.hint">{{ section.hint.text }}</div>
                    </span>
                </div>
                <transition name="fade">
                    <div class="row-input" ref="rowInput" v-if="!loading">
                        <SectionTextInput
                            :accent="accent"
                            :disabled="section.disabled || storeLoading"
                            :error="hasError(section)"
                            :loading="loading"
                            :ref="section.key"
                            :section="section"
                            :success="hasSuccess(section)"
                            @confirmInput="
                                (section, entered) =>
                                    confirmInput(section, entered)
                            "
                            @handleClick="handleClick(section)"
                            @input="onInput"
                            v-if="
                                section.type == 'text-input' ||
                                section.type == 'number-input'
                            "
                            v-model="section.value"
                        ></SectionTextInput>
                        <SectionSingleSelector
                            :accent="accent"
                            :class="{
                                '-error': hasError(section),
                                '-success': hasSuccess(section)
                            }"
                            :disabled="section.disabled || storeLoading"
                            :loading="loading"
                            :options="section.options"
                            :popup="popup"
                            :ref="section.key"
                            :section="section"
                            @click="handleClick(section)"
                            @input="confirmInput(section)"
                            v-if="section.type == 'single-select'"
                            v-model="section.value"
                        ></SectionSingleSelector>
                        <SectionMultiSelector
                            :accent="accent"
                            :disabled="section.disabled || storeLoading"
                            :adding="section.adding"
                            :class="{
                                '-error': hasError(section),
                                '-success': hasSuccess(section)
                            }"
                            :loading="loading"
                            :options="section.options"
                            :popup="popup"
                            :ref="section.key"
                            :section="section"
                            @addItem="ItemAdded"
                            @click="handleClick(section)"
                            @input="confirmInput(section)"
                            v-if="section.type == 'multi-select'"
                            v-model="section.value"
                        ></SectionMultiSelector>
                        <SectionDateSelector
                            :accent="accent"
                            :disabled="section.disabled || storeLoading"
                            :class="{
                                '-error': hasError(section),
                                '-success': hasSuccess(section)
                            }"
                            :format="section.format"
                            :loading="loading"
                            :options="section.options"
                            :ref="section.key"
                            :section="section"
                            @click="handleClick(section)"
                            @input="confirmInput(section)"
                            v-if="section.type == 'date-select'"
                            v-model="section.value"
                        ></SectionDateSelector>
                        <div
                            :accent="accent"
                            :class="{
                                '-disabled': section.disabled || storeLoading
                            }"
                            @click="openModal(section)"
                            class="section-selector"
                            v-if="section.type == 'modal-input'"
                        >
                            <div class="modal-input-value" v-if="section.value">
                                {{ section.value }}
                            </div>
                            <div
                                class="modal-input-value -placeholder"
                                v-if="!section.value"
                            >
                                {{ section.placeholder }}
                            </div>
                        </div>
                    </div>
                </transition>
                <div class="section-error-message" v-if="hasError(section)">
                    {{ errorMessage(section) }}
                </div>
            </div>
            <transition name="fade">
                <div class="section-loading" v-if="loading">
                    <ListLoader></ListLoader>
                </div>
            </transition>
        </div>
    </div>
</template>
<script>
export default {
    components: {
        SectionTextInput: () => import('./SectionTextInput'),
        SectionSingleSelector: () => import('./SectionSingleSelector'),
        SectionMultiSelector: () => import('./SectionMultiSelector'),
        SectionDateSelector: () => import('./SectionDateSelector')
    },
    props: {
        title: String,
        icon: String,
        accent: String,
        sections: Object,
        errors: Object,
        section: String,
        loading: Boolean,
        actions: Array,
        popup: Boolean
    },
    data() {
        return {
            selectedInput: '',
            timeout: null,
            successSection: ''
        }
    },
    computed: {
        filteredSections() {
            const sectionsArray = Object.keys(this.sections).map(
                key => this.sections[key]
            )
            return sectionsArray.filter(s => s.hidden != true)
        },
        storeLoading() {
            return this.$store.state.loading
        }
    },
    methods: {
        async uploadFiles() {
            const sectionsArray = Object.keys(this.sections).map(
                key => this.sections[key]
            )
            const promises = sectionsArray
                .filter(s => s.type == 'attachments-input')
                .map(section => {
                    return this.$refs[section.key][0].uploadFiles()
                })
            return Promise.all(promises)
        },
        sectionTitle(section) {
            return section.required === false
                ? `${section.title} (${this.$t('LABEL_OPTIONAL')})`
                : section.title
        },
        focusInput(section) {
            if (this.$refs[section] && this.$refs[section][0].focusInput()) {
                this.$refs[section][0].focusInput()
            }
        },
        emitAction(action) {
            this.$emit('action', action)
        },
        switched(section) {
            this.$emit('switched', section)
            this.confirmInput(section)
        },
        addAttachment(payload, section) {
            this.handleClick(section)
            this.$emit('addAttachment', payload)
        },
        deleteAttachment(payload) {
            this.$emit('deleteAttachment', payload)
        },
        openModal(section) {
            this.$emit('openModal', section)
        },
        ItemAdded(section) {
            this.$emit(`addItem`, section)
        },
        onInput() {
            this.$emit('input', this.sections)
        },
        errorMessage(section) {
            return this.errors.getMessage(section.key)
        },
        hasError(section) {
            return this.errors.has(section.key, this.section)
        },
        hasSuccess(section) {
            return this.successSection == section.key
        },
        success(section) {
            clearTimeout(this.timeout)
            if (!this.$refs[section.key]) {
                return
            }
            this.successSection = section.key
            this.timeout = setTimeout(() => {
                this.successSection = ''
            }, 2000)
            this.selectedInput = ''
        },
        error(section) {
            clearTimeout(this.timeout)
            if (!this.$refs[section.key]) {
                return
            }
            this.errors.set(section.key)
        },
        handleClick(section) {
            this.selectedInput = section.key
            this.errors.clear(section.key)
            if (
                section.type == 'text-input' ||
                section.type == 'number-input'
            ) {
                this.$nextTick(() => {
                    this.$refs[section.key][0].selectionStart =
                        section.value.length
                })
            }
        },
        confirmInput(section, entered) {
            this.$forceUpdate()
            this.errors.clear(section.key)
            this.$emit('entered', section, this.section)
            if (entered) {
                this.$emit('enter')
            }
        },
        displayPlaceholder(input) {
            return input.placeholder
                ? input.placeholder
                : this.$t('PLACEHOLDER_TYPE_HERE')
        }
    }
}
</script>
<style lang="scss" scoped>
.section-content {
    width: 100%;
    height: 100%;
    position: relative;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
}
.section-row {
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    @media (min-width: 1000px) {
        &.-size-3 {
            width: calc((100% / 2) * 3);
        }
        &.-size-2 {
            width: 50%;
        }
        &.-size-1 {
            width: calc(100% / 3);
        }
    }
}
.row-title {
    padding: 0.5rem 0;
    box-sizing: border-box;
    width: 100%;
    font-family: $input-family;
}
.row-input {
    display: flex;
    flex: 1;
    box-sizing: border-box;
    width: 100%;
    margin-bottom: 1.4rem;
}
@keyframes success {
    0% {
        border-color: $border-accent;
    }
    50% {
        border-color: $main-accent;
    }
    100% {
        border-color: $border-accent;
    }
}
@keyframes error {
    0% {
        border-color: $border-accent;
    }
    50% {
        border-color: $accent-red;
    }
    100% {
        border-color: $border-accent;
    }
}
.section-loading {
    height: 100%;
    width: 100%;
    position: absolute;
    z-index: $highest;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.788);
    font-size: 5rem;
}
.tools-row {
    display: flex;
}
.item-select {
    width: 100%;
}
.modal-input-value {
    padding: 0 1rem;
    height: 100%;
    display: flex;
    align-items: center;
    font-family: $input-family;
    transition: $ease;
    cursor: pointer;

    &.-placeholder {
        color: $input-placeholder;
    }
}
.list-action {
    display: flex;
    align-items: center;
    font-size: 1rem;
    font-family: $input-family;
    cursor: pointer;
    transition: $ease;
    color: $action-accent;
    margin-left: 1rem;

    &:hover {
        transform: translateY(0.1rem);
    }
}
.row-hint {
    position: relative;
}
.row-hint i {
    color: $action-accent;
    font-size: 1.5rem;
    margin-left: 0.5rem;
    cursor: pointer;
    margin-top: 0.5rem;
}
.row-hint div {
    display: none;
    position: fixed;
    background-color: #fff;
    padding: 0.4rem;
    transition: $ease;
    width: 20rem;
    z-index: $high;
    border: $list-border;
    border-radius: $input-radius;
    box-shadow: $minimal-shadow;
}
.row-hint:hover div {
    display: flex;
}
.section-error-message {
    position: absolute;
    bottom: 0.2rem;
    font-size: 0.8rem;
    color: red;
}
</style>
